import React, { useEffect, useState, useCallback } from 'react';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';
import { RichText, htmlHasText, htmlToPlain } from '../common/RichText';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';

interface KernboodschapModuleProps {
  project: Project;
}

interface PretestRow {
  naam: string;
  groep: string;
  sentiment: 'positief' | 'neutraal' | 'negatief';
  verwachteReactie: string;
  aandachtspunt: string;
}

interface Synthese {
  algemeneReactie: string;
  sterk: string;
  aandachtspunten: string;
  verbeteradvies: string;
}

const cardStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 };
const hintStyle: React.CSSProperties = { fontSize: 12.5, color: '#8a90a0', marginBottom: 10 };
function areaStyle(disabled: boolean, minHeight = 100): React.CSSProperties {
  return { width: '100%', minHeight, resize: 'vertical', padding: '10px 12px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, lineHeight: 1.55, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}

const SENTIMENT_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  positief: { bg: '#e9f8ef', color: '#1f7a44', label: 'Positief' },
  neutraal: { bg: '#eef1f6', color: '#4b5563', label: 'Neutraal' },
  negatief: { bg: '#fdecec', color: '#b91c1c', label: 'Negatief' },
};

export const KernboodschapModule: React.FC<KernboodschapModuleProps> = ({ project }) => {
  const toast = useToast();
  const [kernboodschap, setKernboodschap] = useState('');
  const [deelboodschappen, setDeelboodschappen] = useState<string[]>([]);
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [pretesting, setPretesting] = useState(false);
  const [pretest, setPretest] = useState<PretestRow[] | null>(null);
  const [synthese, setSynthese] = useState<Synthese | null>(null);
  const [abOpen, setAbOpen] = useState(false);
  const [abA, setAbA] = useState('');
  const [abB, setAbB] = useState('');
  const [abBusy, setAbBusy] = useState(false);
  const [abResult, setAbResult] = useState<{ vergelijking: any[]; aanbeveling: any } | null>(null);

  const locked = status === 'locked';

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const section = res?.dossier?.sections?.kernboodschap;
      if (section?.content) {
        setKernboodschap(section.content.kernboodschap || '');
        setDeelboodschappen(Array.isArray(section.content.deelboodschappen) ? section.content.deelboodschappen : []);
        setPretest(Array.isArray(section.content.pretest) ? section.content.pretest : null);
        setSynthese(section.content.synthese || null);
        setAbResult(section.content.abResult || null);
        setStatus(section.status === 'locked' ? 'locked' : 'open');
        setLockedAt(section.lockedAt || null);
      }
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { loadDossier(); }, [loadDossier]);

  // Beheerders kunnen deze fase leegmaken om in een testsessie opnieuw te vullen.
  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'kernboodschap', "Kernboodschap", {}, loadDossier);

  // Sla ook de pretest-/A-B-onderbouwing op — het "waarom gedragen" hoort in het levende dossier.
  const contentPayload = () => ({
    kernboodschap,
    deelboodschappen: deelboodschappen.filter((d) => d.trim()),
    pretest: pretest || undefined,
    synthese: synthese || undefined,
    abResult: abResult || undefined,
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      await dossierAPI.saveSection(project.id, 'kernboodschap', contentPayload());
      toast.success('Concept opgeslagen', 'De kernboodschap is als concept vastgelegd.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setSaving(false); }
  };

  const runPretest = async () => {
    if (!htmlHasText(kernboodschap)) {
      toast.error('Nog geen boodschap', 'Schrijf eerst de kernboodschap voordat je de pretest doet.');
      return;
    }
    setPretesting(true);
    try {
      const res = await dossierAPI.pretestKernboodschap(project.id, htmlToPlain(kernboodschap), deelboodschappen.map(htmlToPlain));
      const p = res?.pretest || [];
      const s = res?.synthese || null;
      setPretest(p);
      setSynthese(s);
      // Meteen vastleggen zodat de onderbouwing bewaard blijft na herladen.
      try { await dossierAPI.saveSection(project.id, 'kernboodschap', { kernboodschap, deelboodschappen: deelboodschappen.filter((d) => d.trim()), pretest: p, synthese: s, abResult: abResult || undefined }); } catch (e) { /* niet kritisch */ }
      toast.success('Pretest uitgevoerd', "AI heeft de reacties van de persona's gesimuleerd en vastgelegd.");
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Probeer het later opnieuw.';
      toast.error('Pretest mislukt', msg);
    } finally { setPretesting(false); }
  };

  const openAB = () => { setAbA(kernboodschap); setAbB(''); setAbResult(null); setAbOpen(true); };
  const runAB = async () => {
    if (!abA.trim() || !abB.trim()) { toast.error('Twee varianten nodig', 'Vul zowel variant A als variant B in.'); return; }
    setAbBusy(true);
    try {
      const res = await dossierAPI.pretestKernboodschapAB(project.id, abA, abB);
      const ab = { vergelijking: res?.vergelijking || [], aanbeveling: res?.aanbeveling || {} };
      setAbResult(ab);
      try { await dossierAPI.saveSection(project.id, 'kernboodschap', { kernboodschap, deelboodschappen: deelboodschappen.filter((d) => d.trim()), pretest: pretest || undefined, synthese: synthese || undefined, abResult: ab }); } catch (e) { /* niet kritisch */ }
    } catch (e: any) {
      toast.error('Vergelijken mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setAbBusy(false); }
  };
  const takeVariant = (which: 'A' | 'B') => {
    setKernboodschap(which === 'A' ? abA : abB);
    toast.success('Overgenomen', `Variant ${which} staat nu in de kernboodschap.`);
    setAbOpen(false);
  };

  const updateDeel = (idx: number, value: string) => setDeelboodschappen((list) => list.map((d, i) => (i === idx ? value : d)));
  const addDeel = () => setDeelboodschappen((list) => [...list, '']);
  const removeDeel = (idx: number) => setDeelboodschappen((list) => list.filter((_, i) => i !== idx));

  const canLock = Boolean(htmlHasText(kernboodschap));

  const doLock = async () => {
    setLocking(true);
    try {
      const res = await dossierAPI.lockSection(project.id, 'kernboodschap', contentPayload());
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      toast.success('Fase gevalideerd', 'De kernboodschap is vastgelegd. Het Strategisch Fundament is voltooid.');
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'kernboodschap');
      setStatus('open');
      toast.success('Fase heropend', 'Je kunt de kernboodschap nu aanpassen en opnieuw valideren.');
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  return (
    <div style={{ padding: '32px', maxWidth: 900, margin: '0 auto' }}>
      <FaseTag nr={5} />
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>De kernboodschap</h1>
      <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Opdrachtgever en adviseur stellen de centrale kernboodschap <strong>zelf</strong> op. AI genereert
        de boodschap niet, maar toetst deze tegen de vastgelegde persona's — het virtuele
        communicatiepanel — zodat je toon en inhoud kunt bijstellen.
      </p>

      {/* Boodschappeneditor */}
      <div style={cardStyle}>
        <label style={labelStyle}>Centrale kernboodschap</label>
        <div style={hintStyle}>De ene centrale boodschap die je in alle communicatie consistent wilt overbrengen.</div>
        <RichText value={kernboodschap} disabled={locked} onChange={(html) => setKernboodschap(html)} placeholder="Schrijf de centrale kernboodschap…" minHeight={110} />
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={labelStyle}>Deelboodschappen <span style={{ fontWeight: 400, color: '#8a90a0' }}>(optioneel)</span></div>
        <div style={hintStyle}>Ondersteunende boodschappen die de kernboodschap onderbouwen voor specifieke doelgroepen of thema's.</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {deelboodschappen.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <textarea value={d} disabled={locked} onChange={(e) => updateDeel(i, e.target.value)} style={areaStyle(locked, 52)} placeholder={`Deelboodschap ${i + 1}`} />
              {!locked && (
                <button onClick={() => removeDeel(i)} title="Verwijderen" style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>
          ))}
        </div>
        {!locked && (
          <button onClick={addDeel} style={{ marginTop: 10, padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px dashed #c3c9d4', background: '#fff', color: '#374151', cursor: 'pointer' }}>+ Deelboodschap toevoegen</button>
        )}
      </div>

      {/* Persona-pretest */}
      <div style={{ ...cardStyle, marginTop: 16, background: faseKleur(5).soft, borderColor: faseKleur(5).border }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ ...labelStyle, marginBottom: 2 }}>Persona-pretest</div>
            <div style={{ ...hintStyle, marginBottom: 0 }}>AI simuleert hoe de vastgelegde persona's op je kernboodschap reageren.</div>
          </div>
          <button
            onClick={runPretest}
            disabled={pretesting || !htmlHasText(kernboodschap)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: pretesting || !kernboodschap.trim() ? 'default' : 'pointer', opacity: pretesting || !kernboodschap.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}
          >
            {pretesting ? 'AI toetst…' : 'Pretest uitvoeren'}
          </button>
        </div>

        {pretest && (
          <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
            {pretest.length === 0 && <div style={{ color: '#8a90a0', fontSize: 13 }}>Geen resultaten.</div>}
            {pretest.map((r, i) => {
              const s = SENTIMENT_STYLE[r.sentiment] || SENTIMENT_STYLE.neutraal;
              return (
                <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{r.naam || 'Persona'}</span>
                    {r.groep && <span style={{ fontSize: 12.5, color: '#8a90a0' }}>· {r.groep}</span>}
                    <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 700, color: s.color, background: s.bg, borderRadius: 6, padding: '3px 9px' }}>{s.label}</span>
                  </div>
                  {r.verwachteReactie && <p style={{ margin: '0 0 6px', fontSize: 13.5, color: '#374151', lineHeight: 1.55 }}>{r.verwachteReactie}</p>}
                  {r.aandachtspunt && <p style={{ margin: 0, fontSize: 13, color: '#33417a' }}><strong>Aandachtspunt:</strong> {r.aandachtspunt}</p>}
                </div>
              );
            })}
          </div>
        )}

        {synthese && (
          <div style={{ marginTop: 16, background: '#fff', border: '1px solid #1a1a2e', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 }}>Totaaladvies</div>
            {pretest && pretest.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                {(['positief', 'neutraal', 'negatief'] as const).map((k) => {
                  const n = pretest.filter((r) => r.sentiment === k).length;
                  const st = SENTIMENT_STYLE[k];
                  return <span key={k} style={{ fontSize: 12, fontWeight: 600, color: st.color, background: st.bg, borderRadius: 6, padding: '4px 10px' }}>{st.label}: {n}</span>;
                })}
              </div>
            )}
            {synthese.algemeneReactie && <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#374151', lineHeight: 1.55 }}>{synthese.algemeneReactie}</p>}
            {synthese.sterk && <p style={{ margin: '0 0 8px', fontSize: 13, color: '#1f7a44' }}><strong>Sterk:</strong> {synthese.sterk}</p>}
            {synthese.aandachtspunten && <p style={{ margin: '0 0 8px', fontSize: 13, color: '#9a6a00' }}><strong>Aandachtspunten:</strong> {synthese.aandachtspunten}</p>}
            {synthese.verbeteradvies && (
              <div style={{ marginTop: 6, padding: 12, borderRadius: 8, background: '#f6f8ff', border: '1px solid #dbe4ff' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#33417a', marginBottom: 4 }}>Verbeteradvies</div>
                <p style={{ margin: 0, fontSize: 13.5, color: '#2b3350', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{synthese.verbeteradvies}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* A/B-toets */}
      {!locked && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ ...labelStyle, marginBottom: 2 }}>A/B-toets</div>
              <div style={{ ...hintStyle, marginBottom: 0 }}>Vergelijk twee varianten van de kernboodschap tegen het communicatiepanel en kies de sterkste.</div>
            </div>
            {!abOpen && (
              <button onClick={openAB} style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap' }}>Varianten vergelijken</button>
            )}
          </div>

          {abOpen && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Variant A</label>
                  <textarea value={abA} onChange={(e) => setAbA(e.target.value)} style={areaStyle(false, 90)} placeholder="Variant A…" />
                </div>
                <div>
                  <label style={labelStyle}>Variant B</label>
                  <textarea value={abB} onChange={(e) => setAbB(e.target.value)} style={areaStyle(false, 90)} placeholder="Variant B…" />
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button onClick={runAB} disabled={abBusy} style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#33417a', color: '#fff', cursor: abBusy ? 'default' : 'pointer', opacity: abBusy ? 0.7 : 1 }}>{abBusy ? 'AI vergelijkt…' : 'Vergelijk varianten'}</button>
                <button onClick={() => setAbOpen(false)} style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Sluiten</button>
              </div>

              {abResult && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ padding: 14, borderRadius: 10, background: '#f6f8ff', border: '1px solid #dbe4ff', marginBottom: 12 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#33417a', marginBottom: 6 }}>
                      Aanbeveling: {abResult.aanbeveling?.winnaar === 'gelijk' || !abResult.aanbeveling?.winnaar ? 'geen duidelijke winnaar' : `variant ${abResult.aanbeveling.winnaar}`}
                    </div>
                    {abResult.aanbeveling?.onderbouwing && <p style={{ margin: '0 0 8px', fontSize: 13.5, color: '#2b3350', lineHeight: 1.55 }}>{abResult.aanbeveling.onderbouwing}</p>}
                    {abResult.aanbeveling?.verbeteradvies && <p style={{ margin: '0 0 10px', fontSize: 13, color: '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}><strong>Verbeteradvies:</strong> {abResult.aanbeveling.verbeteradvies}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => takeVariant('A')} style={{ padding: '7px 13px', borderRadius: 7, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Neem variant A over</button>
                      <button onClick={() => takeVariant('B')} style={{ padding: '7px 13px', borderRadius: 7, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Neem variant B over</button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 8 }}>
                    {abResult.vergelijking.map((row: any, i: number) => {
                      const sa = SENTIMENT_STYLE[row.sentimentA] || SENTIMENT_STYLE.neutraal;
                      const sb = SENTIMENT_STYLE[row.sentimentB] || SENTIMENT_STYLE.neutraal;
                      return (
                        <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{row.naam || 'Persona'}</span>
                            {row.groep && <span style={{ fontSize: 12.5, color: '#8a90a0' }}>· {row.groep}</span>}
                            <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 700, color: '#33417a', background: '#eef1ff', borderRadius: 6, padding: '3px 9px' }}>
                              {row.voorkeur === 'gelijk' ? 'Geen voorkeur' : `Voorkeur: variant ${row.voorkeur}`}
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                              <div style={{ fontSize: 11.5, fontWeight: 700, color: sa.color, background: sa.bg, borderRadius: 6, padding: '2px 8px', display: 'inline-block', marginBottom: 4 }}>A · {sa.label}</div>
                              <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{row.reactieA}</p>
                            </div>
                            <div>
                              <div style={{ fontSize: 11.5, fontWeight: 700, color: sb.color, background: sb.bg, borderRadius: 6, padding: '2px 8px', display: 'inline-block', marginBottom: 4 }}>B · {sb.label}</div>
                              <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{row.reactieB}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!locked && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={saveDraft} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Opslaan…' : 'Concept opslaan'}
          </button>
        </div>
      )}

      <LockBar
        status={status}
        onLock={doLock}
        onUnlock={doUnlock}
        lockedAt={lockedAt}
        canLock={canLock}
        busy={locking}
        faseLabel="Kernboodschap"
        canClear={canClear}
        onClear={doClear}
        clearing={clearing}
      />
      {!canLock && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Schrijf de centrale kernboodschap om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
